<template>
  <v-dialog
    :model-value="modelValue"
    max-width="420"
    persistent
    class="recharge-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card rounded="xl" class="user-dialog-card recharge-dialog-card">
      <div class="dialog-header d-flex align-center justify-space-between recharge-dialog__header">
        <div class="d-flex align-center ga-2 min-w-0">
          <div class="dialog-header__icon recharge-dialog__header-icon">
            <v-icon color="primary" size="22">mdi-wallet-plus</v-icon>
          </div>
          <div class="min-w-0">
            <span class="dialog-header__title d-block">Recharge credits</span>
            <span class="recharge-dialog__subtitle">Yandle · Secure checkout</span>
          </div>
        </div>
        <v-btn
          icon
          variant="text"
          size="small"
          class="dialog-close-btn"
          :disabled="saving"
          rounded="lg"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="dialog-body recharge-dialog__body">
        <v-form ref="formRef" v-model="isValid" @submit.prevent="submit">
          <v-text-field
            v-model.number="form.amount"
            class="recharge-dialog__amount"
            label="Amount (Credits)"
            type="number"
            min="1000"
            max="10000"
            step="1"
            prepend-inner-icon="mdi-wallet-plus"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :rules="amountRules"
            rounded="lg"
            hint="1,000 – 10,000 credits · 18% GST on base amount"
            persistent-hint
          />
        </v-form>

        <div class="recharge-dialog__summary">
          <div class="recharge-dialog__summary-row">
            <span>Base</span>
            <span>{{ baseAmount != null ? formatRupee(baseAmount) : '—' }}</span>
          </div>
          <div class="recharge-dialog__summary-row">
            <span>GST (18%)</span>
            <span>{{ gstAmount != null ? formatRupee(gstAmount) : '—' }}</span>
          </div>
          <div class="recharge-dialog__summary-row recharge-dialog__summary-row--total">
            <span>Total</span>
            <span>{{ totalPayable != null ? formatRupee(totalPayable) : '—' }}</span>
          </div>
        </div>

        <div class="recharge-dialog__methods">
          <div class="recharge-dialog__methods-row">
            <div class="recharge-dialog__method">
              <v-icon size="16">mdi-credit-card-outline</v-icon>
              <span>Cards</span>
            </div>
            <div class="recharge-dialog__method">
              <v-icon size="16">mdi-qrcode-scan</v-icon>
              <span>UPI</span>
            </div>
            <div class="recharge-dialog__method">
              <v-icon size="16">mdi-bank-outline</v-icon>
              <span>Netbanking</span>
            </div>
            <div class="recharge-dialog__method">
              <v-icon size="16">mdi-wallet-outline</v-icon>
              <span>Wallets</span>
            </div>
          </div>
          <p class="recharge-dialog__trust mb-0">
            <v-icon size="13" class="recharge-dialog__trust-icon">mdi-lock-outline</v-icon>
            Encrypted · Powered by Razorpay
          </p>
        </div>
      </v-card-text>

      <v-card-actions class="dialog-footer recharge-dialog__footer">
        <v-btn
          variant="text"
          size="small"
          class="text-none dialog-btn dialog-btn--muted"
          :disabled="saving"
          rounded="lg"
          @click="close"
        >
          Cancel
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          size="small"
          class="text-none btn-primary-cta"
          :loading="saving"
          :disabled="!isValid"
          rounded="lg"
          prepend-icon="mdi-lock-outline"
          @click="submit"
        >
          {{ totalPayable != null ? `Pay ${formatRupee(totalPayable)}` : 'Proceed' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { getYandleLogoCheckoutUrl } from '@/components/Extras/YandleLogo.vue'
import createRazorpayOrder from '@/mixins/Credits/createRazorpayOrder'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { formatRupee } from '@/utils/format'
import { loadRazorpayCheckout } from '@/utils/razorpayCheckout'

const RAZORPAY_KEY_ID = 'rzp_test_0G04VfoBpRiYtt'
const MIN_AMOUNT = 1000
const MAX_AMOUNT = 10000
const GST_RATE = 0.18
const PAYMENT_MERCHANT_NAME = 'Yandle'

function normalizeRazorpayContact(org = {}) {
  // org_full_number is a write-once snapshot from onboarding, never touched
  // again by an org profile edit -- using it here would hand Razorpay a
  // stale number (and any OTP/receipt tied to it) after the org changes
  // their contact number. org_country_code/org_contact_number are the
  // fields an edit actually updates, so derive from those only. See
  // OrgDetailsPage.vue's phoneDisplay for the same fix and the live bug
  // that exposed this (2026-08-15).
  const code = String(org.org_country_code ?? '').replace(/\D/g, '')
  const num = String(org.org_contact_number ?? '').replace(/\D/g, '')
  if (!num) return ''

  const combined = code ? `${code}${num}` : num
  if (combined.length > 10 && combined.startsWith('91')) {
    return combined.slice(-10)
  }
  return combined
}

export default {
  name: 'RequestCreditsDialog',
  mixins: [createRazorpayOrder],
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'saved', 'error'],
  data() {
    return {
      saving: false,
      isValid: false,
      form: {
        amount: null,
      },
      amountRules: [
        (v) => (v != null && v !== '' ? true : 'Amount is required'),
        (v) => {
          const amount = Number(v)
          if (!Number.isFinite(amount)) return 'Enter a valid amount'
          if (amount < MIN_AMOUNT || amount > MAX_AMOUNT) {
            return `Amount must be between ${MIN_AMOUNT.toLocaleString('en-IN')} and ${MAX_AMOUNT.toLocaleString('en-IN')} credits`
          }
          return true
        },
      ],
    }
  },
  computed: {
    ...mapGetters(['sessionContext']),
    ...mapState(['organization', 'userName', 'userEmail']),
    payerName() {
      return String(this.userName ?? '').trim()
    },
    payerEmail() {
      return String(this.userEmail ?? '').trim()
    },
    razorpayContact() {
      return normalizeRazorpayContact(this.organization)
    },
    baseAmount() {
      const amount = Number(this.form.amount)
      if (!Number.isFinite(amount) || amount < MIN_AMOUNT || amount > MAX_AMOUNT) {
        return null
      }
      return amount
    },
    gstAmount() {
      return this.baseAmount == null ? null : this.baseAmount * GST_RATE
    },
    totalPayable() {
      return this.baseAmount == null ? null : this.baseAmount * (1 + GST_RATE)
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.resetForm()
    },
  },
  methods: {
    formatRupee,
    resetForm() {
      this.form = { amount: null }
      this.$refs.formRef?.resetValidation?.()
    },
    close() {
      if (this.saving) return
      this.$emit('update:modelValue', false)
    },
    openRazorpayCheckout(orderData) {
      if (typeof window.Razorpay !== 'function') {
        this.$emit('error', { message: 'Payment gateway failed to load. Please refresh and try again.' })
        this.saving = false
        return
      }

      const options = {
        key: RAZORPAY_KEY_ID,
        amount: orderData.amount,
        currency: orderData.currency || 'INR',
        name: PAYMENT_MERCHANT_NAME,
        description: 'Credits recharge',
        image: getYandleLogoCheckoutUrl(),
        order_id: orderData.order_id,
        handler: (response) => this.onRazorpaySuccess(response),
        prefill: {
          name: this.payerName,
          email: this.payerEmail,
          contact: this.razorpayContact,
        },
        notes: {
          org_id: orderData.org_id || '',
        },
        theme: {
          color: '#01224e',
        },
      }

      const rzp = new window.Razorpay(options)
      rzp.on('payment.failed', (response) => this.onRazorpayFailed(response))
      rzp.open()
      this.saving = false
    },
    onRazorpaySuccess(response) {
      this.$emit('saved', response)
      this.$emit('update:modelValue', false)
    },
    onRazorpayFailed(response) {
      this.$emit('error', {
        message:
          response?.error?.description ||
          response?.error?.reason ||
          'Payment failed. Please try again.',
        response,
      })
    },
    async submit() {
      const valid = await this.$refs.formRef?.validate?.()
      if (valid === false) return

      const { org_id } = this.sessionContext ?? {}
      if (!org_id) {
        this.$emit('error', { message: 'Session is not ready.' })
        return
      }

      this.saving = true
      try {
        const response = await this.createRazorpayOrder({
          org_id,
          amount: Number(this.form.amount),
        })

        if (!isApiSuccess(response)) {
          this.$emit('error', response)
          return
        }

        const orderData = response?.data
        if (!orderData?.order_id) {
          this.$emit('error', {
            message: getApiMessage(response, 'Payment order could not be created.'),
          })
          return
        }

        try {
          await loadRazorpayCheckout()
        } catch {
          this.$emit('error', { message: 'Payment gateway failed to load. Please try again.' })
          return
        }

        this.openRazorpayCheckout(orderData)
      } catch (err) {
        this.$emit('error', err)
      } finally {
        if (this.saving) {
          this.saving = false
        }
      }
    },
  },
}
</script>

<style scoped>
.recharge-dialog__header {
  padding-top: var(--y-space-2);
  padding-bottom: var(--y-space-2);
}

.recharge-dialog__header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: var(--y-radius-md);
  background: rgba(99, 102, 241, 0.2);
}

.recharge-dialog__subtitle {
  display: block;
  margin-top: 1px;
  font-size: 0.6875rem;
  color: var(--y-text-muted);
}

.recharge-dialog__body {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding-top: 0.5rem !important;
  padding-bottom: 0.625rem !important;
}

.recharge-dialog__amount :deep(input[type='number']) {
  -moz-appearance: textfield;
  appearance: textfield;
}

.recharge-dialog__amount :deep(input[type='number']::-webkit-outer-spin-button),
.recharge-dialog__amount :deep(input[type='number']::-webkit-inner-spin-button) {
  -webkit-appearance: none;
  margin: 0;
}

.recharge-dialog__amount :deep(.v-messages) {
  font-size: 0.6875rem;
  opacity: 0.75;
}

.recharge-dialog__summary {
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid var(--y-glass-border);
  background: var(--y-panel-bg);
}

.recharge-dialog__summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 3px 0;
  font-size: 0.75rem;
  color: var(--y-text-muted);
}

.recharge-dialog__summary-row--total {
  margin-top: 4px;
  padding-top: 8px;
  border-top: 1px solid var(--y-glass-border);
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--y-text);
}

.recharge-dialog__summary-row--total span:last-child {
  color: rgb(167, 243, 208);
}

.recharge-dialog__methods-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 6px;
}

.recharge-dialog__method {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 8px 4px;
  border-radius: 8px;
  border: 1px solid var(--y-glass-border);
  background: rgba(15, 23, 42, 0.35);
  color: var(--y-text-muted);
  font-size: 0.625rem;
  font-weight: 500;
  letter-spacing: 0.01em;
}

.recharge-dialog__trust {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  margin-top: 8px;
  font-size: 0.6875rem;
  color: var(--y-text-muted);
}

.recharge-dialog__trust-icon {
  flex-shrink: 0;
  opacity: 0.85;
}

.recharge-dialog__footer {
  padding-top: 0.375rem !important;
  padding-bottom: 0.625rem !important;
}
</style>
